/**
 * Profile.jsx — Page de profil et paramètres du compte utilisateur
 *
 * Onglets disponibles :
 *  - infos      : modifier prénom, nom, email, téléphone, ville, date de naissance, sexe
 *  - security   : changer le mot de passe avec indicateur de force
 *  - photo      : uploader une photo de profil (soumise à validation admin)
 *  - danger     : supprimer le compte (confirmation en 2 étapes + countdown 5s)
 *
 * Les données initiales viennent du localStorage, puis sont rafraîchies depuis
 * le serveur au montage pour avoir les informations à jour.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
	FiAlertTriangle,
	FiArrowLeft,
	FiCalendar,
	FiCamera,
	FiCheckCircle,
	FiClock,
	FiEye,
	FiEyeOff,
	FiImage,
	FiMail,
	FiShield,
	FiTrash2,
	FiUser,
	FiX
} from 'react-icons/fi';
import {
	generateAvatarColor,
	getInitials,
	getPasswordStrength,
	validatePasswordForm,
	validateProfileForm
} from './Profile.js';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import BottomNav from '../shared/BottomNav.jsx';
import './Profile.css';

const apiBaseUrl = getApiBaseUrl();

// Définition des onglets de navigation latérale
const profileTabs = [
	{ id: 'infos',        label: 'Informations personnelles', icon: FiUser },
	{ id: 'reservations', label: 'Mes réservations',          icon: FiCalendar },
	{ id: 'security',     label: 'Securite & mot de passe',   icon: FiShield },
	{ id: 'photo',        label: 'Photo de profil',           icon: FiCamera },
	{ id: 'danger',       label: 'Supprimer le compte',       icon: FiTrash2 }
];

function Profile() {
	const navigate = useNavigate();
	const location = useLocation();
	const fileInputRef = useRef(null);

	// Lecture de l'utilisateur connecté depuis localStorage
	const authRaw = window.localStorage.getItem('exp-auth-user');
	const authUser = authRaw ? JSON.parse(authRaw) : null;

	// ── État principal ────────────────────────────────────────────────────────
	const VALID_TABS = ['infos', 'reservations', 'security', 'photo', 'danger'];
	const initialTab = VALID_TABS.includes(location.state?.tab) ? location.state.tab : 'infos';
	const [activeTab, setActiveTab] = useState(initialTab);
	const [profileData, setProfileData] = useState({
		prenom: authUser?.firstName || '',
		nom: authUser?.lastName || '',
		email: authUser?.email || '',
		tel: authUser?.phoneNumber || '',
		ville: authUser?.city || '',
		dateNaissance: authUser?.birthDate || '',
		sexe: authUser?.gender || '',
		avatar: ''
	});
	const [profileErrors, setProfileErrors] = useState({});
	const [profileMessage, setProfileMessage] = useState('');

	// ── État sécurité / mot de passe ──────────────────────────────────────────
	const [passwordForm, setPasswordForm] = useState({
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	});
	const [passwordErrors, setPasswordErrors] = useState({});
	const [passwordMessage, setPasswordMessage] = useState('');
	const [showPasswords, setShowPasswords] = useState({ current: false, next: false, confirm: false });

	// ── État photo de profil ──────────────────────────────────────────────────
	const [photoError, setPhotoError] = useState('');
	const [photoMessage, setPhotoMessage] = useState('');
	const [previewPhoto, setPreviewPhoto] = useState('');        // URL.createObjectURL (blob local)
	const [selectedPhotoDataUrl, setSelectedPhotoDataUrl] = useState(''); // base64 pour l'API
	const [hasPendingPhoto, setHasPendingPhoto] = useState(false);
	const [photoModeration, setPhotoModeration] = useState({ status: 'none', rejectionReason: '' });
	const [signupPhotoConfirmed, setSignupPhotoConfirmed] = useState(authUser?.signupPhotoConfirmed || false);

	// ── État réservations ─────────────────────────────────────────────────────
	const [reservations, setReservations] = useState([]);
	const [resLoading, setResLoading] = useState(false);
	const [resError, setResError] = useState('');

	// ── État suppression de compte ────────────────────────────────────────────
	const [dangerConfirm, setDangerConfirm] = useState(false);
	const [dangerInput, setDangerInput] = useState('');
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteCountdown, setDeleteCountdown] = useState(5); // 5s de délai anti-accident
	const [deleteToast, setDeleteToast] = useState('');

	/**
	 * Countdown de suppression : compte à rebours de 5s après ouverture du modal.
	 * Le bouton "Confirmer" est désactivé tant que deleteCountdown > 0.
	 */
	useEffect(() => {
		if (!deleteModalOpen) {
			setDeleteCountdown(5);
			return undefined;
		}
		if (deleteCountdown <= 0) return undefined;

		const timer = window.setTimeout(() => {
			setDeleteCountdown((previous) => previous - 1);
		}, 1000);

		return () => window.clearTimeout(timer);
	}, [deleteModalOpen, deleteCountdown]);

	// Libère le blob URL de la photo preview quand le composant se démonte
	useEffect(() => {
		return () => {
			if (previewPhoto) URL.revokeObjectURL(previewPhoto);
		};
	}, [previewPhoto]);

	/**
	 * Chargement des données depuis le serveur au montage.
	 * Complète les données déjà présentes dans localStorage (plus fraîches).
	 * Ne bloque pas l'affichage si le réseau est indisponible (try/catch silencieux).
	 */
	useEffect(() => {
		const loadProfileFromServer = async () => {
			if (!authUser?.id) return;

			try {
				const response = await fetch(`${apiBaseUrl}/api/users/${authUser.id}`);
				const data = await response.json();
				if (!response.ok || !data.success) return;

				const serverUser = data.user;
				// Merge avec l'état actuel : on ne remplace que les champs renvoyés par le serveur
				setProfileData((previous) => ({
					...previous,
					prenom:        serverUser.firstName    || previous.prenom,
					nom:           serverUser.lastName     || previous.nom,
					email:         serverUser.email        || previous.email,
					tel:           serverUser.phoneNumber  || previous.tel,
					ville:         serverUser.city         || previous.ville,
					dateNaissance: serverUser.birthDate    || previous.dateNaissance,
					sexe:          serverUser.gender       || previous.sexe,
					avatar:        serverUser.profilePhotoUrl || previous.avatar
				}));
				setPhotoModeration(serverUser.photoModeration || { status: 'none', rejectionReason: '' });
				if (typeof serverUser.signupPhotoConfirmed === 'boolean') {
					setSignupPhotoConfirmed(serverUser.signupPhotoConfirmed);
				}

				// Affiche un message si la photo a été refusée par un admin
				if (serverUser.photoModeration?.status === 'rejected') {
					setPhotoMessage(`Votre photo a ete refusee. Motif: ${serverUser.photoModeration.rejectionReason || 'non precise'}`);
				}
			} catch {
				// Ignore les erreurs réseau pour garder la page utilisable hors-ligne
			}
		};

		loadProfileFromServer();
	}, [authUser?.id]);

	// Charge les réservations quand l'onglet est actif
	useEffect(() => {
		if (activeTab !== 'reservations' || !authUser?.id) return;
		const load = async () => {
			setResLoading(true);
			setResError('');
			try {
				const res = await fetch(`${apiBaseUrl}/api/reservations?userId=${authUser.id}`);
				const data = await res.json();
				if (!res.ok) throw new Error(data.message || 'Erreur chargement');
				setReservations(Array.isArray(data) ? data : (data.reservations || []));
			} catch (e) {
				setResError(e.message || 'Impossible de charger les réservations.');
			} finally {
				setResLoading(false);
			}
		};
		load();
	}, [activeTab, authUser?.id]);

	// Calcule la force du mot de passe en temps réel (via Profile.js)
	const passwordStrength = useMemo(() => getPasswordStrength(passwordForm.newPassword), [passwordForm.newPassword]);

	// Valeurs dérivées pour l'affichage de l'avatar
	const displayedAvatar   = previewPhoto || profileData.avatar;
	const displayedInitials = getInitials(profileData.prenom, profileData.nom);
	const avatarColor       = generateAvatarColor(`${profileData.prenom}${profileData.nom}`);
	const roleLabel         = authUser?.role === 'admin' ? 'Administrateur' : authUser?.role === 'agency' ? 'Agence' : 'Utilisateur';

	// Redirige vers /login si aucun utilisateur connecté
	if (!authUser) return <Navigate to="/login" replace />;

	// ── Handlers informations personnelles ───────────────────────────────────

	const handleProfileChange = (event) => {
		const { name, value } = event.target;
		setProfileData((previous) => ({ ...previous, [name]: value }));
		setProfileErrors((previous) => ({ ...previous, [name]: '' }));
		setProfileMessage('');
	};

	const handleProfileSubmit = (event) => {
		event.preventDefault();
		const validation = validateProfileForm(profileData);
		setProfileErrors(validation.errors);
		setProfileMessage('');
		if (!validation.valid) return;

		const saveProfile = async () => {
			try {
				console.log('Envoi de la mise à jour du profil...');
				const response = await fetch(`${apiBaseUrl}/api/users/${authUser.id}/profile`, {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						requestUserId: authUser.id,
						firstName:  profileData.prenom,
						lastName:   profileData.nom,
						email:      profileData.email,
						phoneNumber: profileData.tel,
						city:       profileData.ville,
						birthDate:  profileData.dateNaissance,
						gender:     profileData.sexe
					})
				});

				console.log('Réponse du serveur:', { ok: response.ok, status: response.status });
				const data = await response.json();
				console.log('Données reçues:', data);

				if (!response.ok || !data.success) {
					const errorMsg = data.message || `Erreur serveur (${response.status})`;
					console.error('Erreur mise à jour:', errorMsg);
					setProfileMessage(`Erreur: ${errorMsg}`);
					setProfileErrors((previous) => ({
						...previous,
						...(data.message ? { general: data.message } : {})
					}));
					return;
				}

				// Met à jour localStorage avec les nouvelles données du serveur
				const mergedAuth = { ...authUser, ...data.user };
				window.localStorage.setItem('exp-auth-user', JSON.stringify(mergedAuth));
				window.localStorage.setItem('exp-user-profile', JSON.stringify({ ...profileData, id: authUser.id }));

				// Rafraîchit l'état local depuis la réponse serveur
				setProfileData((previous) => ({
					...previous,
					prenom:        data.user.firstName  || previous.prenom,
					nom:           data.user.lastName   || previous.nom,
					email:         data.user.email      || previous.email,
					tel:           data.user.phoneNumber || previous.tel,
					ville:         data.user.city       || previous.ville,
					dateNaissance: data.user.birthDate  || previous.dateNaissance,
					sexe:          data.user.gender     || previous.sexe
				}));
				setProfileMessage('Profil mis à jour avec succès ✓');
				console.log('Profil mis à jour avec succès');
			} catch (error) {
				const msg = error.message || 'Erreur réseau';
				console.error('Exception lors de la sauvegarde:', error);
				setProfileMessage(`Erreur: ${msg}`);
				setProfileErrors((previous) => ({ ...previous, general: msg }));
			}
		};

		saveProfile();
	};

	// ── Handlers mot de passe ────────────────────────────────────────────────

	const handlePasswordChange = (event) => {
		const { name, value } = event.target;
		setPasswordForm((previous) => ({ ...previous, [name]: value }));
		setPasswordErrors((previous) => ({ ...previous, [name]: '' }));
		setPasswordMessage('');
	};

	const handlePasswordSubmit = (event) => {
		event.preventDefault();
		const validation = validatePasswordForm(
			passwordForm.currentPassword,
			passwordForm.newPassword,
			passwordForm.confirmPassword
		);
		setPasswordErrors(validation.errors);
		setPasswordMessage('');
		if (!validation.valid) return;

		const savePassword = async () => {
			try {
				const response = await fetch(`${apiBaseUrl}/api/users/${authUser.id}/password`, {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						currentPassword:  passwordForm.currentPassword,
						newPassword:      passwordForm.newPassword,
						confirmPassword:  passwordForm.confirmPassword
					})
				});

				const data = await response.json();
				if (!response.ok || !data.success) {
					setPasswordErrors({ currentPassword: data.message || 'Mot de passe actuel incorrect.' });
					return;
				}

				setPasswordMessage('Mot de passe mis a jour avec succes ✓');
				setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
			} catch {
				setPasswordErrors({ currentPassword: 'Impossible de sauvegarder le mot de passe.' });
			}
		};

		savePassword();
	};

	// ── Handlers photo de profil ─────────────────────────────────────────────

	const handlePhotoPick = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validation du format et de la taille côté client
		const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			setPhotoError('Format invalide. Utilisez JPG, PNG ou WEBP.');
			setPhotoMessage('');
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			setPhotoError('Fichier trop lourd (max 5 MB).');
			setPhotoMessage('');
			return;
		}

		if (previewPhoto) URL.revokeObjectURL(previewPhoto);

		// Lecture en DataURL (base64) pour l'envoi à l'API + blob URL pour la preview
		const fileReader = new FileReader();
		fileReader.onload = () => {
			setPreviewPhoto(URL.createObjectURL(file));
			setSelectedPhotoDataUrl(String(fileReader.result || ''));
			setHasPendingPhoto(true);
			setPhotoModeration((previous) => ({ ...previous, status: 'pending' }));
			setPhotoError('');
			setPhotoMessage('Apercu charge. Cliquez sur Enregistrer la photo.');
		};
		fileReader.onerror = () => {
			setPhotoError('Impossible de lire cette image.');
			setPhotoMessage('');
		};

		fileReader.readAsDataURL(file);
	};

	const handlePhotoSave = async () => {
		if (!hasPendingPhoto || !selectedPhotoDataUrl || !authUser?.id) return;
		setPhotoError('');
		setPhotoMessage('');

		try {
			const response = await fetch(`${apiBaseUrl}/api/users/${authUser.id}/photo-request`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ photoDataUrl: selectedPhotoDataUrl, requestUserId: authUser.id })
			});

			let data = null;
			try { data = await response.json(); } catch { data = null; }

			if (!response.ok || !data.success) {
				setPhotoError(
					data?.message ||
					(response.status === 413
						? 'Image trop lourde pour le serveur. Reduisez la taille puis reessayez.'
						: 'Erreur lors de l envoi de la photo.')
				);
				return;
			}

			// Photo envoyée : libère le blob local et attend la validation admin
			setHasPendingPhoto(false);
			if (previewPhoto) { URL.revokeObjectURL(previewPhoto); setPreviewPhoto(''); }
			setSelectedPhotoDataUrl('');
			setPhotoModeration(data.photoModeration || { status: 'pending' });
			setPhotoMessage('Photo envoyee pour validation admin. Elle sera visible apres approbation.');
		} catch {
			setPhotoError('Impossible de contacter le serveur pour envoyer la photo.');
		}
	};

	const handlePhotoDelete = () => {
		if (previewPhoto) URL.revokeObjectURL(previewPhoto);
		setPreviewPhoto('');
		setSelectedPhotoDataUrl('');
		setHasPendingPhoto(false);
		setPhotoModeration({ status: 'none', rejectionReason: '' });
		setPhotoMessage('Selection supprimee.');
		setPhotoError('');
	};

	// Ferme la bannière de rejet de photo (appel API silencieux)
	const handleDismissRejection = async () => {
		if (!authUser?.id || photoModeration.status !== 'rejected') return;
		try {
			await fetch(`${apiBaseUrl}/api/users/${authUser.id}/photo-rejection/dismiss`, { method: 'PATCH' });
		} catch {
			// Ignore les erreurs réseau pour ne pas bloquer l'UI
		}
		setPhotoModeration({ status: 'none', rejectionReason: '' });
		setPhotoMessage('');
	};

	// ── Suppression de compte ─────────────────────────────────────────────────

	// Le bouton "Confirmer la suppression" est actif uniquement si :
	// - la case est cochée ET le mot "SUPPRIMER" est saisi exactement
	const canDeleteAccount = dangerConfirm && dangerInput === 'SUPPRIMER';

	const confirmDeleteAccount = () => {
		// Suppression locale (on ne fait pas d'appel API ici — côté client uniquement)
		window.localStorage.removeItem('exp-auth-user');
		window.localStorage.removeItem('exp-user-profile');
		setDeleteToast('Compte supprime');
		setDeleteModalOpen(false);
		window.setTimeout(() => navigate('/'), 900);
	};

	return (
		<div className="ux-page ux-profile-page">
			{/* Toast de confirmation de suppression */}
			{deleteToast ? <div className="ux-profile-toast">{deleteToast}</div> : null}

			{/* ── En-tête de page ── */}
			<section className="ux-shell ux-profile-header" aria-label="Entete profil">
				<button type="button" className="ux-btn ux-btn-ghost ux-profile-back-btn" onClick={() => navigate(-1)}>
					<FiArrowLeft size={16} />
					Retour
				</button>
				<div className="ux-profile-header-copy">
					<p>Mon espace</p>
					<h1>Profil et securite</h1>
				</div>
				<div className="ux-profile-header-meta">
					<span className="ux-profile-role-pill">{roleLabel}</span>
					<span>{profileData.email}</span>
				</div>
			</section>

			<div className="ux-shell ux-profile-layout">

				{/* ── Sidebar gauche : avatar + navigation par onglets ── */}
				<aside className="ux-profile-sidebar">
					<div className="ux-profile-user-card">
						<div className="ux-profile-avatar" style={{ backgroundColor: avatarColor }}>
							{displayedAvatar
								? <img src={displayedAvatar} alt="Avatar" />
								: <span>{displayedInitials}</span>
							}
						</div>
						<h2>{profileData.prenom} {profileData.nom}</h2>
						<p>{profileData.email}</p>
					</div>

					<nav className="ux-profile-tabs" aria-label="Navigation profil">
						{profileTabs.map((tab) => {
							const Icon = tab.icon;
							return (
								<button
									type="button"
									key={tab.id}
									className={`ux-profile-tab ${activeTab === tab.id ? 'is-active' : ''}`}
									onClick={() => setActiveTab(tab.id)}
								>
									<Icon size={16} />
									<span>{tab.label}</span>
								</button>
							);
						})}
					</nav>
				</aside>

				{/* ── Contenu central selon l'onglet actif ── */}
				<section className="ux-profile-content">

					{/* Bannière si la photo a été refusée par un admin */}
					{photoModeration.status === 'rejected' && photoModeration.rejectionReason ? (
						<div className="ux-profile-rejected-banner">
							<div>
								<strong>Photo de profil refusee par un admin.</strong>
								<p>Motif: {photoModeration.rejectionReason}</p>
							</div>
							<button type="button" className="ux-btn ux-btn-ghost" onClick={handleDismissRejection}>
								<FiX size={14} />
								Fermer
							</button>
						</div>
					) : null}

					{/* ── Onglet Informations personnelles ── */}
					{activeTab === 'infos' ? (
						<form className="ux-profile-card ux-profile-fade" onSubmit={handleProfileSubmit}>
							<h3>Informations personnelles</h3>
							<div className="ux-profile-grid">
								<label className="ux-profile-field">
									<span>Prenom</span>
									<input name="prenom" value={profileData.prenom} onChange={handleProfileChange} />
									{profileErrors.prenom ? <small>{profileErrors.prenom}</small> : null}
								</label>
								<label className="ux-profile-field">
									<span>Nom</span>
									<input name="nom" value={profileData.nom} onChange={handleProfileChange} />
									{profileErrors.nom ? <small>{profileErrors.nom}</small> : null}
								</label>
								<label className="ux-profile-field ux-profile-col-full">
									<span>Email</span>
									<input name="email" type="email" value={profileData.email} onChange={handleProfileChange} />
									<em>Un email de confirmation sera envoye</em>
									{profileErrors.email ? <small>{profileErrors.email}</small> : null}
								</label>
								<label className="ux-profile-field">
									<span>Telephone</span>
									<input name="tel" type="tel" value={profileData.tel} onChange={handleProfileChange} placeholder="+212XXXXXXXXX" />
									{profileErrors.tel ? <small>{profileErrors.tel}</small> : null}
								</label>
								<label className="ux-profile-field">
									<span>Ville</span>
									<input name="ville" value={profileData.ville} onChange={handleProfileChange} />
								</label>
								<label className="ux-profile-field">
									<span>Date de naissance</span>
									<input name="dateNaissance" type="date" value={profileData.dateNaissance} onChange={handleProfileChange} />
								</label>
								<label className="ux-profile-field">
									<span>Sexe</span>
									<select name="sexe" value={profileData.sexe} onChange={handleProfileChange}>
										<option value="Homme">Homme</option>
										<option value="Femme">Femme</option>
										<option value="Non precise">Non precise</option>
									</select>
									{profileErrors.sexe ? <small>{profileErrors.sexe}</small> : null}
								</label>
							</div>
							<button type="submit" className="ux-btn ux-btn-primary">Enregistrer les modifications</button>
							{profileMessage ? <p className="ux-profile-success">{profileMessage}</p> : null}
						</form>
					) : null}

					{/* ── Onglet Réservations ── */}
					{activeTab === 'reservations' ? (
						<div className="ux-profile-card ux-profile-fade">
							<h3>Mes réservations</h3>
							{resLoading && (
								<p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Chargement…</p>
							)}
							{!resLoading && resError && (
								<p style={{ color: '#b91c1c', fontSize: '0.88rem' }}>{resError}</p>
							)}
							{!resLoading && !resError && reservations.length === 0 && (
								<div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
									<FiCalendar size={32} style={{ color: '#d1d5db', marginBottom: '0.75rem' }} />
									<p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Aucune réservation pour le moment.</p>
								</div>
							)}
							<div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
								{reservations.map((r) => {
									const statusColor = r.status === 'confirmed' ? '#10b981'
										: r.status === 'pending' ? '#f59e0b'
										: r.status === 'cancelled' ? '#ef4444' : '#6b7280';
									const statusLabel = r.status === 'confirmed' ? 'Confirmée'
										: r.status === 'pending' ? 'En attente'
										: r.status === 'cancelled' ? 'Annulée' : r.status;
									return (
										<div key={r._id || r.id} style={{
											border: '1px solid #e5e7eb', borderRadius: 12,
											padding: '1rem 1.1rem', background: '#fafafa',
											display: 'flex', gap: '1rem', alignItems: 'flex-start',
										}}>
											{r.vehicleImage && (
												<img src={r.vehicleImage} alt="" style={{
													width: 72, height: 56, objectFit: 'cover',
													borderRadius: 8, flexShrink: 0,
												}} />
											)}
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
													<strong style={{ fontSize: '0.95rem', color: '#111827' }}>
														{r.vehicleName || r.productName || 'Véhicule'}
													</strong>
													<span style={{
														fontSize: '0.72rem', fontWeight: 700, padding: '2px 10px',
														borderRadius: 20, background: `${statusColor}18`, color: statusColor,
														border: `1px solid ${statusColor}40`, flexShrink: 0,
													}}>
														{statusLabel}
													</span>
												</div>
												<p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
													<FiClock size={12} style={{ marginRight: 4 }} />
													{r.startDate ? new Date(r.startDate).toLocaleDateString('fr-FR') : '--'}
													{r.endDate ? ` → ${new Date(r.endDate).toLocaleDateString('fr-FR')}` : ''}
												</p>
												{r.totalPrice && (
													<p style={{ fontSize: '0.82rem', color: '#374151', margin: '0.2rem 0 0', fontWeight: 600 }}>
														{r.totalPrice} MAD
													</p>
												)}
												{r.agencyName && (
													<p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.15rem 0 0' }}>
														{r.agencyName}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : null}

					{/* ── Onglet Sécurité ── */}
					{activeTab === 'security' ? (
						<div className="ux-profile-fade ux-profile-stack">
							<form className="ux-profile-card" onSubmit={handlePasswordSubmit}>
								<h3>Securite & mot de passe</h3>
								<div className="ux-profile-password-grid">
									{['current', 'next', 'confirm'].map((key, idx) => {
										const names  = ['currentPassword', 'newPassword', 'confirmPassword'];
										const labels = ['Mot de passe actuel', 'Nouveau mot de passe', 'Confirmer nouveau mot de passe'];
										return (
											<label key={key} className="ux-profile-field ux-profile-password-field">
												<span>{labels[idx]}</span>
												<input
													type={showPasswords[key] ? 'text' : 'password'}
													name={names[idx]}
													value={passwordForm[names[idx]]}
													onChange={handlePasswordChange}
												/>
												<button type="button" onClick={() => setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }))}>
													{showPasswords[key] ? <FiEyeOff size={16} /> : <FiEye size={16} />}
												</button>
												{passwordErrors[names[idx]] ? <small>{passwordErrors[names[idx]]}</small> : null}
											</label>
										);
									})}
								</div>

								{/* Indicateur visuel de force du mot de passe (4 barres colorées) */}
								<div className="ux-profile-strength">
									<div className="ux-profile-strength-track">
										{Array.from({ length: 4 }).map((_, index) => (
											<span
												key={index}
												style={{ background: index < passwordStrength.score ? passwordStrength.color : 'rgba(111, 218, 255, 0.16)' }}
											/>
										))}
									</div>
									<p>Force: {passwordStrength.label}</p>
								</div>

								<ul className="ux-profile-rules">
									<li>✓ Minimum 8 caracteres</li>
									<li>✓ Une majuscule</li>
									<li>✓ Un chiffre</li>
									<li>✓ Un caractere special</li>
								</ul>

								<button type="submit" className="ux-btn ux-btn-primary">Mettre a jour le mot de passe</button>
								{passwordMessage ? <p className="ux-profile-success">{passwordMessage}</p> : null}
							</form>

							{/* Informations de sécurité du compte */}
							<div className="ux-profile-card ux-profile-security-cards">
								<h3>Securite du compte</h3>
								<div className="ux-profile-info-card">
									<FiClock size={16} />
									<div>
										<strong>Derniere connexion</strong>
										<p>Session active</p>
									</div>
								</div>
								<div className="ux-profile-info-card">
									<FiMail size={16} />
									<div>
										<strong>Email de connexion actuel</strong>
										<p>{profileData.email}</p>
									</div>
								</div>
								<button type="button" className="ux-btn ux-profile-btn-red-outline">Se deconnecter de tous les appareils</button>
							</div>
						</div>
					) : null}

					{/* ── Onglet Photo de profil ── */}
					{activeTab === 'photo' ? (
						<div className="ux-profile-card ux-profile-fade">
							<h3>Photo de profil</h3>

							{/* Blocage : photo d'inscription en attente de validation admin */}
							{!signupPhotoConfirmed ? (
								<div style={{
									display: 'flex', flexDirection: 'column', alignItems: 'center',
									gap: '1rem', padding: '2rem 1rem', textAlign: 'center',
								}}>
									<div style={{
										width: 56, height: 56, borderRadius: '50%',
										background: 'rgba(245,158,11,0.12)',
										border: '1.5px solid rgba(245,158,11,0.35)',
										display: 'flex', alignItems: 'center', justifyContent: 'center',
									}}>
										<FiClock size={26} color="#f59e0b" />
									</div>
									<div>
										<p style={{ fontWeight: 700, color: '#f59e0b', marginBottom: '0.4rem', fontSize: '0.95rem' }}>
											Photo d&apos;inscription en attente de validation
										</p>
										<p style={{ color: '#8a93aa', fontSize: '0.82rem', lineHeight: 1.6, maxWidth: 380 }}>
											Votre selfie pris lors de l&apos;inscription est en cours d&apos;examen par l&apos;équipe UltimateX Drive.
											Vous pourrez modifier votre photo de profil une fois celle-ci approuvée.
										</p>
									</div>
									<div style={{
										padding: '0.7rem 1.2rem',
										background: 'rgba(245,158,11,0.08)',
										border: '1px solid rgba(245,158,11,0.2)',
										borderRadius: 10, fontSize: '0.78rem', color: '#d4a053',
									}}>
										La validation prend généralement moins de 24h.
									</div>
								</div>
							) : (
								<>
									<div className="ux-profile-photo-admin-notice">
										<FiCheckCircle size={15} />
										<span>Votre photo sera mise à jour automatiquement sur votre profil après validation par un administrateur.</span>
									</div>
									<div className="ux-profile-photo-wrap">
										<div className="ux-profile-avatar ux-profile-avatar-lg" style={{ backgroundColor: avatarColor }}>
											{displayedAvatar ? <img src={displayedAvatar} alt="Avatar" /> : <span>{displayedInitials}</span>}
										</div>
										<div className="ux-profile-photo-actions">
											{/* Input file caché, déclenché via le bouton "Choisir une photo" */}
											<input
												ref={fileInputRef}
												type="file"
												accept="image/jpeg,image/png,image/webp"
												hidden
												onChange={handlePhotoPick}
											/>
											<button type="button" className="ux-btn ux-btn-ghost" onClick={() => fileInputRef.current?.click()}>
												<FiImage size={16} />
												Choisir une photo
											</button>
											<button type="button" className="ux-btn ux-btn-primary" onClick={handlePhotoSave} disabled={!hasPendingPhoto}>
												Enregistrer la photo
											</button>
											{displayedAvatar ? (
												<button type="button" className="ux-btn ux-btn-ghost" onClick={handlePhotoDelete}>
													Supprimer la photo
												</button>
											) : null}
											<p>Formats acceptes: JPG, PNG, WEBP - max 5 MB</p>
										</div>
									</div>
									{photoError ? <p className="ux-profile-error">{photoError}</p> : null}
									{photoMessage ? <p className="ux-profile-success">{photoMessage}</p> : null}
								</>
							)}
						</div>
					) : null}

					{/* ── Onglet Zone de danger (suppression) ── */}
					{activeTab === 'danger' ? (
						<div className="ux-profile-card ux-profile-fade ux-profile-danger-zone">
							<h3>Supprimer le compte</h3>
							<p>
								Cette action est irreversible. Toutes vos donnees, reservations et favoris seront
								definitivement supprimes.
							</p>
							{/* Deux verrous : case à cocher + saisie du mot "SUPPRIMER" */}
							<label className="ux-profile-danger-check">
								<input type="checkbox" checked={dangerConfirm} onChange={(event) => setDangerConfirm(event.target.checked)} />
								<span>Je comprends que cette action est irreversible</span>
							</label>
							<label className="ux-profile-field">
								<span>Tapez exactement SUPPRIMER</span>
								<input value={dangerInput} onChange={(event) => setDangerInput(event.target.value)} />
							</label>
							<button type="button" className="ux-btn ux-profile-btn-red" disabled={!canDeleteAccount} onClick={() => setDeleteModalOpen(true)}>
								Supprimer definitivement mon compte
							</button>
						</div>
					) : null}
				</section>
			</div>

			{/* ── Modal de confirmation finale (avec countdown 5s) ── */}
			{deleteModalOpen ? (
				<div className="ux-profile-modal-backdrop">
					<div className="ux-profile-modal">
						<h4>Confirmation finale</h4>
						<p>Vous pourrez confirmer dans {deleteCountdown}s.</p>
						<div className="ux-profile-modal-actions">
							<button type="button" className="ux-btn ux-btn-ghost" onClick={() => setDeleteModalOpen(false)}>
								<FiX size={15} />
								Annuler
							</button>
							{/* Bouton actif uniquement quand le countdown est terminé */}
							<button type="button" className="ux-btn ux-profile-btn-red" disabled={deleteCountdown > 0} onClick={confirmDeleteAccount}>
								<FiAlertTriangle size={15} />
								Confirmer la suppression
							</button>
						</div>
					</div>
				</div>
			) : null}

			<BottomNav />
		</div>
	);
}

export default Profile;
